import { useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import "../../InvoiceFormGoods.scss";
import { useGetInvoiceConfig } from "api/queries/invoice/useGetInvoiceConfig";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import generateRows from "./rowGenerator";

/**
 * An individual row of data
 * Contains the name of the row, the requested value and the actual value
 */
function Row({ data }: any) {
  const { name, requestedValue, actualValue, formatFn, isCustomsInfo } = data;
  const formattedRequestedValue = formatFn ? formatFn(requestedValue) : requestedValue;
  const formattedActualValue = formatFn ? formatFn(actualValue) : actualValue;

  // If the row is a customs info row, we compact the row so that the data is visually grouped together
  // And change the font-weight of the row name to be normal instead of bold
  const labelClass = isCustomsInfo ? "goods-compact-td" : "font-b9";
  const valueClass = isCustomsInfo ? "goods-compact-td" : "";

  return (
    <tr>
      <td className={labelClass}>{name}</td>
      <td className={valueClass}>{formattedRequestedValue}</td>
      <td className={valueClass}>{formattedActualValue}</td>
    </tr>
  );
}

/**
 * A table containing the requested and actual values of the commodity
 * Renders in the subcomponent of the goods table when a row is expanded
 */
function GoodsRowSubComponent({ commodityData }: any) {
  const intl = useIntl();
  const { id: shipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const { data: invoiceConfig, isLoading: isConfigLoading } = useGetInvoiceConfig({
    shipmentId: shipmentId!,
    currentInvoiceId: currentInvoiceId!,
    options: { refetchOnMount: false },
  });

  const rows = generateRows(commodityData, invoiceConfig, intl);

  if (isConfigLoading) return <LoadingSkeleton rows={2} />;
  return (
    <table className="goods-subcomponent-table">
      <thead>
        <tr>
          <th>
            <FormattedMessage id="general.name" defaultMessage="Name" />
          </th>
          <th>
            <FormattedMessage id="general.requested" defaultMessage="Requested" />
          </th>
          <th>
            <FormattedMessage id="general.actual" defaultMessage="Actual" />
          </th>
        </tr>
      </thead>
      <tbody>{rows.map((row) => row.shouldRender && <Row key={row.name} data={row} />)}</tbody>
    </table>
  );
}

export default GoodsRowSubComponent;
